import React from 'react';
import type { PriceTargetsWidgetInstanceConfig, PriceTargetsData } from './types';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner, formatPrice, formatPercent } from '../../../design';
import { KeyMetricBadge } from '../../../blocks/chart-widget';
import { useDataProviderWidget } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned).
const SAMPLE_DATA: PriceTargetsData = {
  ticker: 'SAMPLE',
  consensus_rating: 'Buy',
  avg_price_target: 324.31,
  target_analyst_count: 13,
  strong_buy: 1, buy: 69, hold: 33, sell: 7, strong_sell: 0,
  close_price: 308.82,
};

interface Props {
  config: PriceTargetsWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const n = (v: number | null | undefined) => (typeof v === 'number' ? v : 0);

function ratingTone(rating: string | null): string {
  const r = (rating || '').toLowerCase();
  if (r.includes('buy')) return 'bg-success-50 text-success-700 border-success-200';
  if (r.includes('sell')) return 'bg-danger-50 text-danger-700 border-danger-200';
  return 'bg-neutral-100 text-neutral-600 border-neutral-200';
}

const PriceTargetsWidgetRenderer: React.FC<Props> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useDataProviderWidget<PriceTargetsData>(
    'price_targets',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const d = uid ? data : SAMPLE_DATA;

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (!d) return null;

  const buyN = n(d.strong_buy) + n(d.buy);
  const holdN = n(d.hold);
  const sellN = n(d.sell) + n(d.strong_sell);
  const total = buyN + holdN + sellN;
  const pct = (x: number) => (total > 0 ? (x / total) * 100 : 0);

  const upside =
    d.avg_price_target != null && d.close_price != null && d.close_price !== 0
      ? (d.avg_price_target - d.close_price) / d.close_price
      : null;
  const upsideTone = upside == null ? 'text-neutral-400' : upside >= 0 ? 'text-success-600' : 'text-danger-600';

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}

      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">Price Targets</span>
        {d.consensus_rating && (
          <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${ratingTone(d.consensus_rating)}`}>
            {d.consensus_rating}
          </span>
        )}
        {d.target_analyst_count != null && (
          // Analysts who issued PRICE TARGETS recently (FMP last-quarter
          // window, month fallback) — deliberately smaller than the ratings
          // bar below, which counts the street's full active grades. Label
          // the scope so the mismatch doesn't read as a bug.
          <div
            className="ml-auto"
            title="Analysts issuing price targets in the last quarter — the Buy/Hold/Sell bar counts the street's full active ratings"
          >
            <KeyMetricBadge label="Targets (Qtr)" value={String(d.target_analyst_count)} />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-3 space-y-4">
        {/* Target + implied upside */}
        <div className="flex items-end gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-400">Avg Target</div>
            <div className="text-xl font-semibold text-neutral-900">{formatPrice(d.avg_price_target)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-400">Implied Upside</div>
            <div className={`text-xl font-semibold ${upsideTone}`}>
              {upside == null ? '--' : `${upside >= 0 ? '+' : ''}${formatPercent(upside * 100, 1)}`}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wide text-neutral-400">Current</div>
            <div className="text-sm text-neutral-600">{formatPrice(d.close_price)}</div>
          </div>
        </div>

        {/* Rating distribution */}
        <div>
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-neutral-100">
            <div className="bg-success-500" style={{ width: `${pct(buyN)}%` }} title={`Buy: ${buyN}`} />
            <div className="bg-neutral-300" style={{ width: `${pct(holdN)}%` }} title={`Hold: ${holdN}`} />
            <div className="bg-danger-500" style={{ width: `${pct(sellN)}%` }} title={`Sell: ${sellN}`} />
          </div>
          <div className="mt-2 flex justify-between text-xs text-neutral-600">
            <span><span className="font-semibold text-success-600">{buyN}</span> Buy</span>
            <span><span className="font-semibold text-neutral-500">{holdN}</span> Hold</span>
            <span><span className="font-semibold text-danger-600">{sellN}</span> Sell</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceTargetsWidgetRenderer;
